import { buildBasicFieldLayoutRows } from '@/utils/printBasicFieldsLayout'
import {
  mergeTableConfigFromRaw,
  getNormalizedTableColumnWidthPercents,
  shouldBlankMoneyCellForPrint,
  getTableColumnsForPrint,
  getMergedTableHeaderCells,
  mergedTableHeaderPresentationStyle,
  effectiveTableHeaderTextAlign,
  isTableRowHeightLocked
} from '@/utils/printTableLayout'
import { getTotalRowCellText, shouldShowTotalsRow, PRINT_PREVIEW_LEGACY_COLUMNS } from '@/utils/printTableTotals'
import { migrateSignatureItems, displayLabelForItem } from '@/utils/printSignatures'
import { buildPaymentFooterDisplayLines } from '@/utils/paymentFooterBankFields'
import {
  paymentFooterFontSizePx,
  useStatementDualSignatureGrid,
  statementDualGridRowPairs,
  resolveStatementDualGridCellValue,
  formatDualGridLabel
} from '@/utils/printStatementTailBlocks'
import {
  shouldRenderHeaderLines,
  shouldRenderFooterLines,
  footerLinesPlainProbe
} from '@/utils/printHeaderFooterLines'
import { mergeTemplatePage } from '@/utils/exportDeliveryOrderPrintExcel'
import { pickWarehouseManualCode } from '@/utils/statementLines'

function pickWarehouseFrom (obj) {
  return pickWarehouseManualCode(obj)
}

export const PRINT_TEMPLATE_TYPE = 'payment_request'

/**
 * 账单明细展示用材料名：优先客户材料名称。
 */
export function resolveStatementLineMaterialName (ln) {
  const clientMat = String(ln.client_material_name || '').trim()
  if (clientMat) return clientMat
  const product = String(ln.product_name || '').trim()
  if (product) return product
  const ms = String(ln.material_summary || '').trim()
  const sum = String(ln.summary || '').trim()
  const looksLikeOrderRef = (s) => /销售订单/.test(s) && /项次/.test(s)
  const ok = (s) => s && !looksLikeOrderRef(s)
  if (ok(ms)) return ms
  if (ok(sum)) return sum
  return ''
}

/** 账单明细：客户英文颜色；无客户色时用内部英文色 */
export function resolveStatementLineClientColorEn (ln) {
  const client = String(ln?.client_color_en ?? '').trim()
  if (client) return client
  return String(ln?.color_en ?? '').trim()
}

/** 账单明细：客户中文颜色；无客户色时用内部中文色 */
export function resolveStatementLineClientColorCn (ln) {
  const client = String(ln?.client_color_cn ?? '').trim()
  if (client) return client
  return String(ln?.color_cn ?? '').trim()
}

function mmToInch (mm) {
  return (Number(mm) || 0) / 25.4
}

function excelPaperAndMargins (page) {
  const p = mergeTemplatePage(page)
  const paperSizeMap = { A4: 9, A3: 8, Letter: 1 }
  const paperSize = paperSizeMap[p.paperSize] || 9
  const orientation = p.orientation === 'landscape' ? 'landscape' : 'portrait'
  return {
    paperSize,
    orientation,
    margins: {
      left: mmToInch(p.marginLeft ?? 10),
      right: mmToInch(p.marginRight ?? 10),
      top: mmToInch(p.marginTop ?? 10),
      bottom: mmToInch(p.marginBottom ?? 10),
      header: 0.2,
      footer: 0.35
    }
  }
}

function richHtmlToPlainText (html) {
  if (!html) return ''
  let s = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
  if (typeof document !== 'undefined') {
    const d = document.createElement('div')
    d.innerHTML = s
    return (d.textContent || '')
      .split('\n')
      .map((l) => l.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n')
      .trim()
  }
  return s.replace(/<[^>]+>/g, '\n').replace(/\n+/g, '\n').trim()
}

function templateFooterPlain (html) {
  if (!html || typeof html !== 'string') return ''
  if (typeof document !== 'undefined') {
    const d = document.createElement('div')
    d.innerHTML = html
    return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim()
  }
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function normalizeExcelFooterText (text) {
  if (!text) return ''
  let t = String(text)
  t = t.replace(/\{page_number\}/gi, '&P')
  t = t.replace(/\{total_pages\}/gi, '&N')
  return t.trim()
}

/** Excel 应按数值写入的字段（非文本） */
const EXCEL_NUMBER_FIELDS = new Set([
  'serial',
  'quantity',
  'pack_quantity',
  'unit_price',
  'line_amount',
  'item_amount',
  'amount',
  'dye_fee',
  'debit',
  'credit',
  'balance',
  'gw_kg',
  'nw_kg',
  'cbm'
])

const EXCEL_MONEY_FIELDS = new Set([
  'unit_price',
  'line_amount',
  'item_amount',
  'amount',
  'debit',
  'credit',
  'balance'
])

function excelNumFmtForField (field) {
  if (field === 'dye_fee' || field === 'serial') return '0'
  if (EXCEL_MONEY_FIELDS.has(field)) return '0.00'
  if (field === 'quantity' || field === 'pack_quantity') return '0.####'
  return 'General'
}

/**
 * 将单元格值写成 Excel 数字（可计算）；非数字字段保持原样。
 */
function applyExcelCellValue (cell, field, raw) {
  if (raw === null || raw === undefined || raw === '') {
    cell.value = ''
    return
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    cell.value = raw
    if (EXCEL_NUMBER_FIELDS.has(field)) {
      cell.numFmt = excelNumFmtForField(field)
    }
    return
  }
  if (EXCEL_NUMBER_FIELDS.has(field) && raw !== '-') {
    const n = Number(String(raw).replace(/,/g, '').trim())
    if (Number.isFinite(n)) {
      cell.value = n
      cell.numFmt = excelNumFmtForField(field)
      return
    }
  }
  cell.value = raw
}

function getSingleItemContext (printData) {
  const arr = printData?.items
  if (!Array.isArray(arr) || arr.length !== 1) return null
  return arr[0]
}

export function replaceStatementVariables (text, printData) {
  if (!text || !printData) return text || ''
  let result = String(text)
  Object.keys(printData).forEach((key) => {
    if (typeof printData[key] === 'string' || typeof printData[key] === 'number') {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), printData[key])
    }
  })
  const one = getSingleItemContext(printData)
  if (one) {
    Object.keys(one).forEach((key) => {
      if (typeof one[key] === 'string' || typeof one[key] === 'number') {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), one[key])
      }
    })
  }
  result = result.replace(/\{current_date\}/g, new Date().toLocaleDateString('zh-CN'))
  result = result.replace(/\{current_time\}/g, new Date().toLocaleTimeString('zh-CN'))
  return result
}

export function getStatementFieldValue (field, fieldDef, printData) {
  if (!printData) return ''
  if (!field || String(field).trim() === '') return ''
  if (field === '__custom_text') return String(fieldDef?.customValue || '')
  const one = getSingleItemContext(printData)
  let value = printData[field]
  if ((value === null || value === undefined || value === '') && field !== 'items') {
    if (one && Object.prototype.hasOwnProperty.call(one, field)) {
      value = one[field]
    }
  }
  if (value === null || value === undefined) return ''
  return String(value)
}

function resolvePaymentFooterField (pf, fieldKey) {
  if (!pf) return ''
  if (pf[fieldKey]) return String(pf[fieldKey])
  const selected = (pf.accounts || []).find((x) => x.key === pf.selectedAccountKey)
  return selected ? String(selected[fieldKey] || '') : ''
}

function resolveColumnFontSizePx (col, defaultPx) {
  const n = Number(col?.fontSize)
  if (Number.isFinite(n) && n > 0) return n
  return defaultPx
}

function excelHorizontalAlign (col, isHeader) {
  const a = isHeader ? effectiveTableHeaderTextAlign(col) : col?.align || 'left'
  if (a === 'right') return 'right'
  if (a === 'center') return 'center'
  return 'left'
}

/** 与 PrintPreview.getTableCellValue（payment_request）一致 */
export function getStatementTableCellValue (item, col, rowIndex, tableConfig) {
  const field = col.field
  if (field == null || String(field).trim() === '') return ''
  if (shouldBlankMoneyCellForPrint(field, tableConfig)) return ''

  if (field === 'delivery_order_no') {
    return item.delivery_order_no || '-'
  }
  if (field === 'order_no' || field === 'client_purchase_order_no') {
    return item[field] || item.doc_no || '-'
  }
  if (field === 'delivery_date') {
    return item.delivery_date || item.biz_date || '-'
  }
  if (field === 'order_date') {
    const raw = item.order_date
    if (raw === undefined || raw === null || raw === '') return '-'
    return String(raw).slice(0, 10)
  }
  if (field === 'line_status') {
    if (item.line_status === 'pre_shipment') return '预请款'
    if (item.line_status === 'delivered') return '已出库'
    return '-'
  }
  if (field === 'line_status_reason') {
    return item.line_status_reason || '-'
  }
  if (field === 'product_name') {
    const v = resolveStatementLineMaterialName(item)
    return v || item[field] || item.summary || item.material_summary || '-'
  }
  if (field === 'material_summary') {
    const v = resolveStatementLineMaterialName(item)
    return v || item.material_summary || item.summary || '-'
  }
  if (field === 'client_material_name') {
    return item.client_material_name || resolveStatementLineMaterialName(item) || '-'
  }
  if (field === 'warehouse_manual_code' || field === 'batch_no') {
    const map = tableConfig?.enrichmentByLineId
    const lid = Number(item.line_id)
    const fromMap =
      map && Number.isInteger(lid) && lid > 0 ? pickWarehouseFrom(map[lid]) : ''
    const v = pickWarehouseFrom(item) || fromMap
    return v || '-'
  }
  if (field === 'specification' || field === 'color') {
    return item[field] || '-'
  }
  if (field === 'color_en') {
    if (tableConfig?.direction === 'payable') {
      return String(item.color_en ?? '').trim() || '-'
    }
    const v = resolveStatementLineClientColorEn(item)
    return v || '-'
  }
  if (field === 'color_cn') {
    if (tableConfig?.direction === 'payable') {
      return String(item.color_cn ?? '').trim() || '-'
    }
    const v = resolveStatementLineClientColorCn(item)
    return v || '-'
  }
  if (field === 'quantity') {
    const qtyVal = item.quantity ?? item.line_quantity
    if (qtyVal === null || qtyVal === undefined || qtyVal === '') return ''
    const n = Number(qtyVal)
    return Number.isFinite(n) ? n : ''
  }
  if (field === 'unit') {
    return item.unit || '-'
  }
  if (field === 'notes') {
    return item.notes || item.summary || '-'
  }
  if (field === 'dye_fee') {
    const raw = item.dye_fee ?? item.dyeFee
    if (raw === null || raw === undefined || raw === '' || Number(raw) === 0) return ''
    const n = Number(raw)
    return Number.isFinite(n) ? Math.round(n) : ''
  }
  if (col.field === 'serial') {
    const s = item.serial || rowIndex + 1
    const n = Number(s)
    return Number.isFinite(n) ? n : s
  }
  if (col.field === 'color') {
    const en = resolveStatementLineClientColorEn(item)
    const cn = resolveStatementLineClientColorCn(item)
    if (en && cn) return `${en} / ${cn}`
    if (en) return en
    if (cn) return cn
    return item.color || '-'
  }
  if (col.field === 'unit_price' || col.field === 'line_amount' || col.field === 'amount' || col.field === 'item_amount') {
    const v = (() => {
      const f = col.field
      if (f === 'unit_price') return item.unit_price ?? item.unitPrice
      if (f === 'line_amount') return item.line_amount ?? item.item_amount ?? item.amount
      if (f === 'item_amount') return item.item_amount ?? item.line_amount ?? item.amount
      if (f === 'amount') return item.amount ?? item.line_amount ?? item.item_amount
      return item[f]
    })()
    if (v === undefined || v === null || v === '') return ''
    const n = Number(v)
    return Number.isFinite(n) ? n : ''
  }
  if (col.field === 'debit' || col.field === 'credit' || col.field === 'balance') {
    const v = item[col.field]
    if (v === undefined || v === null || v === '') return ''
    const n = Number(v)
    return Number.isFinite(n) ? n : String(v)
  }
  const raw = item[col.field]
  if (raw === undefined || raw === null || raw === '') return '-'
  return raw
}

function normalizeTemplateConfig (raw) {
  const rawTc = raw?.tableConfig || {}
  return {
    fields: [],
    header: '',
    footer: '',
    footerAlign: 'left',
    ...raw,
    page: mergeTemplatePage(raw?.page || {}),
    tableConfig: mergeTableConfigFromRaw(rawTc)
  }
}

/**
 * 拉取账单打印上下文（与「打印预览」同源，可指定 template_id）。
 */
export async function loadStatementPrintContext (statementId, templateId = null) {
  const id = Number(statementId)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('无效的账单 ID')
  }
  const { getPrintData } = await import('@/api/printTemplates')
  const opts = {}
  if (templateId != null && templateId !== '') {
    opts.template_id = templateId
  }
  const res = await getPrintData(PRINT_TEMPLATE_TYPE, id, opts)
  if (!res?.success) {
    throw new Error(res?.message || '获取打印数据失败')
  }
  const tpl = res.data?.template
  const printData = res.data?.print_data
  if (!tpl || !printData) {
    throw new Error(res?.message || '打印模板或数据不完整')
  }
  return {
    templateConfig: normalizeTemplateConfig(tpl.template_config || {}),
    printData,
    template: tpl,
    statementId: id
  }
}

/**
 * 按当前模板配置导出 Excel（与打印预览布局一致）。
 */
export async function exportStatementPrintLayoutWorkbook ({
  statement = {},
  printPayload,
  templateConfig: templateConfigIn = null
}) {
  const templateConfig = templateConfigIn
    ? normalizeTemplateConfig(templateConfigIn)
    : normalizeTemplateConfig({})
  const printData = printPayload || {}
  await writeStatementPrintWorkbook({
    templateConfig,
    printData,
    fileBase: statement.statement_no || printData.statement_no || `statement_${statement.statement_id || ''}`
  })
}

/**
 * 列表导出：先按所选模板拉取打印数据再导出。
 */
export async function exportStatementPrintExcel (statementId, templateId = null) {
  const { templateConfig, printData, statementId: id } = await loadStatementPrintContext(statementId, templateId)
  await writeStatementPrintWorkbook({
    templateConfig,
    printData,
    fileBase: printData.statement_no || `statement_${id}`
  })
}

async function writeStatementPrintWorkbook ({ templateConfig, printData, fileBase }) {
  const tc = templateConfig.tableConfig || {}
  const cols =
    Array.isArray(tc.columns) && tc.columns.length
      ? getTableColumnsForPrint(tc.columns)
      : PRINT_PREVIEW_LEGACY_COLUMNS

  const colCount = Math.max(1, cols.length)
  const pcts = getNormalizedTableColumnWidthPercents(cols)
  const bodyFs = Math.max(9, Math.min(14, Number(templateConfig.page?.headerRootFontSizePx) || 11))

  const { default: ExcelJS } = await import('exceljs')
  const wb = new ExcelJS.Workbook()
  wb.creator = 'ERP'

  const { paperSize, orientation, margins } = excelPaperAndMargins(templateConfig.page)
  const ws = wb.addWorksheet('打印版', {
    pageSetup: {
      paperSize,
      orientation,
      margins,
      fitToWidth: 1,
      fitToHeight: 999,
      fitToPage: true,
      horizontalCentered: true
    },
    views: [{ showGridLines: false }]
  })

  const totalCharBudget = orientation === 'landscape' ? 132 : 94
  for (let i = 1; i <= colCount; i++) {
    const pct = pcts[i - 1] != null ? pcts[i - 1] : 100 / colCount
    ws.getColumn(i).width = Math.max(6.2, Math.round((pct / 100) * totalCharBudget))
  }

  const thin = { style: 'thin', color: { argb: 'FF000000' } }
  const boxBorder = { top: thin, left: thin, bottom: thin, right: thin }
  let r = 1
  const mergeRow = (fromR, fromC, toR, toC) => {
    if (fromC > toC || fromR > toR) return
    ws.mergeCells(fromR, fromC, toR, toC)
  }

  const pageMerged = mergeTemplatePage(templateConfig.page)

  if (shouldRenderHeaderLines(templateConfig)) {
    const lines = templateConfig.headerLines || []
    const plainLines = []
    let maxFs = Math.max(9, Math.min(22, Number(pageMerged.headerRootFontSizePx) || 11))
    for (const line of lines) {
      const raw = replaceStatementVariables(String(line?.content || ''), printData)
      const plain = richHtmlToPlainText(raw).trim()
      if (!plain) continue
      plainLines.push(plain)
      const fsRaw = Number(line?.fontSizePx)
      if (Number.isFinite(fsRaw) && fsRaw > 0) {
        maxFs = Math.max(maxFs, Math.min(22, Math.round(fsRaw)))
      }
    }
    const merged = plainLines.join('\n')
    if (merged) {
      ws.getCell(r, 1).value = merged
      ws.getCell(r, 1).alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: maxFs }
      mergeRow(r, 1, r, colCount)
      ws.getRow(r).height = Math.min(240, Math.max(28, merged.split('\n').length * (maxFs * 1.35)))
      r += 2
    }
  } else {
    const headerPlain = richHtmlToPlainText(replaceStatementVariables(templateConfig.header || '', printData))
    if (headerPlain) {
      const fs = Math.max(9, Math.min(22, Number(pageMerged.headerRootFontSizePx) || 11))
      ws.getCell(r, 1).value = headerPlain
      ws.getCell(r, 1).alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: fs }
      mergeRow(r,  1, r, colCount)
      ws.getRow(r).height = Math.min(240, Math.max(28, headerPlain.split('\n').length * (fs * 1.35)))
      r += 2
    }
  }

  const basicRows = buildBasicFieldLayoutRows(templateConfig.basicFields || [])
  for (const brow of basicRows) {
    if (brow.type === 'split') {
      const leftTxt = brow.left
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getStatementFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('    ')
      const rightTxt = brow.right
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getStatementFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('\n')
      const mid = Math.max(1, Math.floor(colCount / 2))
      ws.getCell(r, 1).value = leftTxt
      ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: bodyFs }
      mergeRow(r, 1, r, mid)
      ws.getCell(r, mid + 1).value = rightTxt
      ws.getCell(r, mid + 1).alignment = { horizontal: 'right', vertical: 'top', wrapText: true }
      ws.getCell(r, mid + 1).font = { size: bodyFs }
      mergeRow(r, mid + 1, r, colCount)
      ws.getRow(r).height = 22
      r++
    } else if (brow.type === 'grid') {
      const fields = brow.fields || []
      const sumSpan = fields.reduce((s, f) => s + Math.max(1, Math.min(12, Number(f.__colSpan ?? f.colSpan ?? 12))), 0) || 12
      let c0 = 1
      for (const f of fields) {
        const span = Math.max(1, Math.min(12, Number(f.__colSpan ?? f.colSpan ?? 12)))
        const wcols = Math.max(1, Math.round((span / sumSpan) * colCount))
        const c1 = Math.min(colCount, c0 + wcols - 1)
        const lbl = f.label ? `${f.label}: ` : ''
        ws.getCell(r, c0).value = `${lbl}${getStatementFieldValue(f.field, f, printData)}${f.suffix || ''}`
        ws.getCell(r, c0).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
        ws.getCell(r, c0).font = { size: resolveColumnFontSizePx(f, bodyFs) }
        if (c1 > c0) mergeRow(r, c0, r, c1)
        c0 = c1 + 1
        if (c0 > colCount) break
      }
      ws.getRow(r).height = 20
      r++
    } else {
      const line = (brow.fields || [])
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getStatementFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('    ')
      ws.getCell(r, 1).value = line
      ws.getCell(r, 1).font = { size: bodyFs }
      ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      mergeRow(r, 1, r, colCount)
      ws.getRow(r).height = 20
      r++
    }
  }
  if (basicRows.length) r++

  const ttm = Math.max(0, Number(templateConfig.page?.tableSectionMarginTopPx ?? 0) || 0)
  if (ttm > 0) {
    ws.getRow(r).height = Math.min(ttm / 1.5, 40)
    r++
  }

  const headerTableRow = r
  getMergedTableHeaderCells(cols).forEach((cell) => {
    const fromC = cell.startIndex + 1
    const toC = fromC + cell.colspan - 1
    const col = cell.col
    const cellRef = ws.getCell(r, fromC)
    cellRef.value = cell.label || ''
    cellRef.font = { bold: true, size: resolveColumnFontSizePx(col, bodyFs) }
    const mergedStyle = mergedTableHeaderPresentationStyle(cell.colspan)
    cellRef.alignment = {
      horizontal: mergedStyle ? 'center' : excelHorizontalAlign(col, true),
      vertical: mergedStyle ? 'middle' : 'middle',
      wrapText: true
    }
    cellRef.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
    cellRef.border = boxBorder
    if (cell.colspan > 1) mergeRow(r, fromC, r, toC)
  })
  const rowLocked = isTableRowHeightLocked(tc)
  const rowH = rowLocked && Number(tc.rowHeight) > 0 ? Number(tc.rowHeight) * 0.75 : null
  ws.getRow(r).height = rowH != null ? rowH : 20
  r++

  const items = Array.isArray(printData.items) ? printData.items : []
  items.forEach((item, idx) => {
    cols.forEach((col, cidx) => {
      const cell = ws.getCell(r, cidx + 1)
      const text = getStatementTableCellValue(item, col, idx, tc)
      applyExcelCellValue(cell, col.field, text)
      cell.font = { size: resolveColumnFontSizePx(col, bodyFs) }
      cell.alignment = { horizontal: excelHorizontalAlign(col, false), vertical: 'top', wrapText: true }
      cell.border = boxBorder
    })
    ws.getRow(r).height = rowH != null ? rowH : 21
    r++
  })

  const totalsCfg = { ...tc, columns: cols }
  if (shouldShowTotalsRow(printData, totalsCfg)) {
    cols.forEach((col, cidx) => {
      const cell = ws.getCell(r, cidx + 1)
      const text = getTotalRowCellText(col, printData, cols, cidx, totalsCfg, PRINT_TEMPLATE_TYPE)
      applyExcelCellValue(cell, col.field, text)
      cell.font = { bold: true, size: resolveColumnFontSizePx(col, bodyFs) }
      cell.alignment = { horizontal: excelHorizontalAlign(col, false), vertical: 'middle', wrapText: true }
      cell.border = boxBorder
    })
    ws.getRow(r).height = rowH != null ? rowH : 20
    r++
  }

  r++

  const notesRaw =
    templateConfig.notes && String(templateConfig.notes.content || '').trim()
      ? String(templateConfig.notes.content).trim()
      : ''
  const notesText = notesRaw ? replaceStatementVariables(notesRaw, printData) : ''
  if (notesText) {
    ws.getCell(r, 1).value = notesText
    ws.getCell(r, 1).font = { size: Math.max(10, bodyFs - 1) }
    ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
    mergeRow(r, 1, r, colCount)
    ws.getRow(r).height = Math.min(180, Math.max(24, notesText.split('\n').length * 14))
    r += 2
  }

  const pf = templateConfig.paymentFooter
  if (pf && pf.enabled !== false) {
    const pfFontSize = paymentFooterFontSizePx(pf)
    ws.getCell(r, 1).value = pf.noticeText || '请将以上货款汇入以下银行账户'
    ws.getCell(r, 1).font = { bold: true, size: pfFontSize }
    mergeRow(r, 1, r, colCount)
    r++
    const bankLines = buildPaymentFooterDisplayLines(pf, (key) => resolvePaymentFooterField(pf, key))
    bankLines.forEach((line) => {
      ws.getCell(r, 1).value = `${line.label}${line.value}`
      ws.getCell(r, 1).font = { size: pfFontSize }
      mergeRow(r, 1, r, colCount)
      r++
    })
    r++
  }

  const dualActive = useStatementDualSignatureGrid(templateConfig.signatures, PRINT_TEMPLATE_TYPE)
  if (dualActive) {
    const mid = Math.max(1, Math.floor(colCount / 2))
    const fetchData = (k) => getStatementFieldValue(k, null, printData)
    for (const pair of statementDualGridRowPairs(templateConfig.signatures?.statementDualGrid)) {
      const leftLbl = formatDualGridLabel(pair.left?.label)
      const rightLbl = formatDualGridLabel(pair.right?.label)
      const leftVal = resolveStatementDualGridCellValue(pair.left, fetchData)
      const rightVal = resolveStatementDualGridCellValue(pair.right, fetchData)
      ws.getCell(r, 1).value = `${leftLbl}${leftVal}`
      ws.getCell(r, 1).font = { size: bodyFs }
      ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      mergeRow(r, 1, r, mid)
      ws.getCell(r, mid + 1).value = `${rightLbl}${rightVal}`
      ws.getCell(r, mid + 1).font = { size: bodyFs }
      ws.getCell(r, mid + 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      mergeRow(r, mid + 1, r, colCount)
      ws.getRow(r).height = 20
      r++
    }
    r++
  } else {
    const sigItems = migrateSignatureItems(templateConfig.signatures)
    if (sigItems.length > 0) {
      const sigLabelRow = r
      const sigValueRow = r + 1
      const n = sigItems.length
      for (let i = 0; i < n; i++) {
        const fromC = Math.floor((i * colCount) / n) + 1
        const toC = Math.floor(((i + 1) * colCount) / n)
        const item = sigItems[i]
        const label = displayLabelForItem(item)
        const val = (() => {
          const fixed = String(item?.fixedName ?? item?.fixed_name ?? '').trim()
          if (fixed) return fixed
          return getStatementFieldValue(item?.dataKey || item?.field, item, printData)
        })()
        ws.getCell(sigLabelRow, fromC).value = `${label}：`
        ws.getCell(sigLabelRow, fromC).alignment = { horizontal: 'center', vertical: 'bottom', wrapText: true }
        ws.getCell(sigValueRow, fromC).value = val
        ws.getCell(sigValueRow, fromC).alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
        if (toC > fromC) {
          mergeRow(sigLabelRow, fromC, sigLabelRow, toC)
          mergeRow(sigValueRow, fromC, sigValueRow, toC)
        }
      }
      r += 2
    }
  }

  const pageText = '第 &P 页，共 &N 页'
  let footerText = ''
  if (shouldRenderFooterLines(templateConfig)) {
    footerText = normalizeExcelFooterText(
      footerLinesPlainProbe(templateConfig, (s) => replaceStatementVariables(String(s), printData), templateFooterPlain)
    )
  } else {
    footerText = normalizeExcelFooterText(templateFooterPlain(templateConfig.footer))
  }
  const hasPageVar = /&P|&N/i.test(footerText)
  ws.headerFooter.oddFooter = footerText ? (hasPageVar ? footerText : `${footerText}    ${pageText}`) : pageText

  if (items.length > 0 && headerTableRow > 0) {
    ws.pageSetup.printTitlesRow = `${headerTableRow}:${headerTableRow}`
  }

  const fileName = `${fileBase || 'statement'}_打印版.xlsx`
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
